import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Empty, Input } from 'antd';

import {
  clearTeamConversations,
  fetchTeamConversation,
  fetchTeamConversations,
  fetchWebChatConversationMessages,
  resetSelectedWebChatConversation,
} from '../../../../reduxToolkit/inboxSlice';
import InfiniteScroll from 'react-infinite-scroll-component';
import { LoadingOutlined } from '@ant-design/icons';
import { showErrorToast } from '../../../../components/commonComponents/gxAntdToast/toastUtils';
import NotificationContext from '../../../../providers/NotificationProvider';
import Conversation from './InboxUtils/ConversationSelection/Conversation';
import OnlineUserList from './InboxUtils/ChatHeader/OnlineUserList';
import { useSocket } from '../../../../providers/SocketProvider';

const { Title } = Typography;
const { Search } = Input;

const ConversationSelectionSider = ({ className }) => {
  // hooks
  const dispatch = useDispatch();
  const toast = useContext(NotificationContext);
  const { sendGetConnectionDetailsEvent } = useSocket();

  // selectors
  const {
    teamConversations,
    teamConversationsCount,
    selectedWebChatTeam,
    selectedWebChatConversation,
    makeNewConversationRequest,
    conversationExcludeList,
    teamConversationsError,
    activeEndUsers,
  } = useSelector((state) => state?.inbox);
  const { currentBrand, currentUser } = useSelector((state) => state?.app);

  // states

  const [page, setPage] = useState(2);
  const [limit] = useState(10);

  // handlers Functions
  const getConversationMessages = (conversation, page = 1, limit = 40) => {
    if (selectedWebChatConversation.id === conversation.id) return;
    const params = {
      brand_id: currentBrand?.brand_id,
      team_id: selectedWebChatTeam?.team_id,
      conversation_id: conversation?.id,
      page,
      limit,
    };
    dispatch(fetchWebChatConversationMessages(params));
    dispatch(resetSelectedWebChatConversation());
  };

  useEffect(() => {
    if (selectedWebChatConversation?.id) {
      if (!activeEndUsers.includes(selectedWebChatConversation?.id)) {
        sendGetConnectionDetailsEvent({
          conversation_id: selectedWebChatConversation?.id,
          brand_id: currentBrand?.brand_id,
        });
      }
    }
  }, [selectedWebChatConversation?.id]);

  const sortedConversations = useMemo(() => {
    return teamConversations
      ? [...teamConversations].sort(
          (a, b) =>
            new Date(b.last_message_time).getTime() -
            new Date(a.last_message_time).getTime(),
        )
      : [];
  }, [teamConversations]);

  const loadMore = () => {
    const excludeListString = conversationExcludeList.join(',');
    const params = {
      brand_id: currentBrand?.brand_id,
      team_id: selectedWebChatTeam?.team_id,
      team_member_id: selectedWebChatTeam?.team_member_id,
      user_id: currentUser?.id,
      excludeListString,
      clearExcludeList: false,
      page,
      limit,
    };
    setPage(page + 1);
    dispatch(fetchTeamConversations(params));
  };

  // useEffects
  useEffect(() => {
    if (makeNewConversationRequest.length > 0) {
      console.log('makeNewConversationRequest', makeNewConversationRequest);
      makeNewConversationRequest.forEach((conversation) => {
        dispatch(
          fetchTeamConversation({
            brand_id: Number(currentBrand?.brand_id),
            team_id: Number(conversation?.team_id),
            conversation_id: Number(conversation?.conversation_id),
            team_member_id: Number(conversation?.team_member_id),
          }),
        );
      });
    }
  }, [makeNewConversationRequest]);

  useEffect(() => {
    const lastSelectedWebChatTeam = localStorage.getItem(
      'lastSelectedWebChatTeam',
    );
    if (lastSelectedWebChatTeam == selectedWebChatTeam?.team_id) return;
    if (teamConversations.length > 0) {
      dispatch(clearTeamConversations());
      setPage(2);
    }
    localStorage.setItem(
      'lastSelectedWebChatTeam',
      selectedWebChatTeam?.team_id,
    );
  }, [selectedWebChatTeam]);

  useEffect(() => {
    if (teamConversationsError) {
      showErrorToast(toast, teamConversationsError);
    }
  }, [teamConversationsError]);

  return (
    <div
      className={`${className} grid grid-rows-[minmax(100px,1fr)_minmax(600px,7fr)] h-dvh bg-white border-r border-r-gray-200`}
    >
      <div
        className="flex flex-col gap-2 items-start justify-between rounded-b-lg bg-white p-4 shadow-[0px_7px_11px_-10px_rgba(0,_0,_0,_0.35)]"
        // style={{
        //   boxShadow: '0px 2px 0px 10px rgba(0, 0, 0, 0.637)',
        // }}
      >
        <div className="flex w-full items-center justify-between mb-1">
          <Title
            level={5}
            className="z-10 text-[#3e4046]"
            style={{ margin: '0', color: '#3e4046' }}
          >
            {selectedWebChatTeam?.team_name}
          </Title>

          <OnlineUserList size="default" count={2} />
        </div>

        <Search
          placeholder="input search text"
          allowClear
          onSearch={() => {}}
          className="w-full"
        />
      </div>

      <div className="p-2 grid grid-flow-row  grid-cols-1 justify-center overflow-auto">
        {teamConversations.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="self-center"
            description={
              selectedWebChatTeam?.team_id
                ? 'No Conversations Found'
                : 'Select a Team'
            }
          />
        ) : (
          <div
            id="scrollableDivSelectionSideBar"
            className="min-h-[600px] h-full overflow-y-auto"
          >
            <InfiniteScroll
              dataLength={teamConversations.length}
              next={loadMore}
              hasMore={teamConversationsCount > teamConversations.length}
              loader={
                <div className="flex justify-center my-2">
                  <LoadingOutlined style={{ fontSize: '32px' }} />
                </div>
              }
              scrollableTarget="scrollableDivSelectionSideBar"
              scrollThreshold={0.9}
            >
              {sortedConversations.map((conversation, i) => {
                const isSelected =
                  conversation.id === selectedWebChatConversation?.id;
                return (
                  <Conversation
                    key={conversation.id}
                    conversation={conversation}
                    isSelected={isSelected}
                    getConversationMessages={getConversationMessages}
                  />
                );
              })}
            </InfiniteScroll>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationSelectionSider;
